/**
 * The experiment's items: 100 per page, sortable by position, latency, cost or
 * any evaluator's score, filtered by status — all in the URL. Clicking a row
 * opens it in the side panel (`?item=`). While the run is live, rows fill in
 * through `useExperimentItems`.
 *
 * "Not applicable for X" is filtered on the loaded page: the contract's items
 * endpoint has no score-status filter.
 */

import { Box } from "@/components/ui/box";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "@/components/ui/data-table";
import { EmptyState, QueryState } from "@/components/ui/states";
import { formatLatency, formatMoney } from "@/lib/format";
import { useUrlState } from "@/lib/use-url-state";
import { useActiveScoringRun, useExperimentItems } from "../hooks";
import { SendToQueueDialog } from "@/features/annotation/send-to-queue-dialog";
import { AddToDatasetDialog } from "./add-to-dataset-dialog";
import { useHumanScoreColumns } from "./human-score-columns";
import { SelectionBar } from "./selection-bar";
import { ItemsToolbar } from "./items-toolbar";
import { OutputCell, ScoreCell } from "./score-cell";
import { isLive } from "../status";
import type { ExperimentSummary, ItemQuery, ItemRow } from "../types";

export function ItemsTable({
  experiment: e,
  selected,
  onSelect,
}: {
  experiment: ExperimentSummary;
  selected: Set<string>;
  onSelect: (ids: Set<string>) => void;
}) {
  const url = useUrlState();
  const page = Number(url.get("page") ?? "1") || 1;
  const query: ItemQuery = {
    status: (url.get("status") ?? undefined) as ItemQuery["status"],
    sort: url.get("sort") ?? undefined,
    order: (url.get("order") ?? undefined) as ItemQuery["order"],
    page,
  };
  const rescoring = !!useActiveScoringRun(e.id);
  const items = useExperimentItems(e.id, query, isLive(e.status) || rescoring);
  const na = url.get("na");
  const rows = (items.data?.items ?? []).filter((r) => !na || r.scores[na]?.status === "not_applicable");
  const total = items.data?.total ?? 0;
  const size = items.data?.page_size ?? 100;
  const first = (page - 1) * size;
  const allTicked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const tick = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelect(next);
  };

  const label = (r: ItemRow) => `${r.position + 1}${e.repetitions > 1 ? ` repetition ${r.repetition}` : ""}`;
  const [adding, setAdding] = useState(false);
  const [annotating, setAnnotating] = useState(false);
  const humanColumns = useHumanScoreColumns(e.id);
  const checkbox: Column<ItemRow> = {
    header: (
      <input
        type="checkbox"
        className="accent-ink"
        aria-label="Select all items on this page"
        checked={allTicked}
        onChange={() => onSelect(allTicked ? new Set() : new Set([...selected, ...rows.map((r) => r.id)]))}
      />
    ),
    headClass: "w-8",
    isolated: true,
    cell: (r) => (
      <input
        type="checkbox"
        className="accent-ink"
        aria-label={`Select item ${label(r)}`}
        checked={selected.has(r.id)}
        onChange={() => tick(r.id)}
      />
    ),
  };
  const runColumns: Column<ItemRow>[] = [
    {
      header: "#",
      sort: "position",
      className: "font-mono text-muted",
      cell: (r) => (
        <button
          type="button"
          aria-label={`Open item ${label(r)}`}
          onClick={(ev) => (ev.stopPropagation(), url.set({ item: r.id }))}
        >
          {r.position + 1}
          {e.repetitions > 1 && <span className="text-dim">·{r.repetition}</span>}
        </button>
      ),
    },
    {
      header: "Input",
      className: "max-w-[320px]",
      cell: (r) => <span className="clamp-2">{r.input_preview}</span>,
    },
    { header: "Output", className: "max-w-[360px]", cell: (r) => <OutputCell row={r} /> },
    ...e.evaluators.map((ev): Column<ItemRow> => ({
      header: ev.name,
      sort: `score:${ev.key}`,
      tag: ev.version,
      headClass: "num",
      className: "text-right",
      cell: (r) => <ScoreCell score={r.scores[ev.key]} row={r} />,
    })),
    ...humanColumns,
    {
      header: "Latency",
      sort: "latency",
      num: true,
      className: "text-muted",
      cell: (r) => formatLatency(r.latency_ms),
    },
    {
      header: "Cost",
      sort: "cost",
      num: true,
      className: "text-muted",
      cell: (r) => formatMoney(r.agent_cost_usd),
    },
  ];
  const columns = [checkbox, ...runColumns];

  return (
    <>
      <ItemsToolbar evaluators={e.evaluators} />
      <SelectionBar
        count={selected.size}
        onAddToDataset={() => setAdding(true)}
        onSendToAnnotation={() => setAnnotating(true)}
        onClear={() => onSelect(new Set())}
      />
      <Box>
        <QueryState query={items} what="items">
          {rows.length === 0 ? (
            <EmptyState
              title={e.status === "collecting" ? "Collecting traces…" : "No items match these filters."}
            />
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              onRowClick={(r) => url.set({ item: r.id })}
              className="tbl text-xs"
            />
          )}
          <div className="flex items-center gap-2 border-t border-line-soft bg-well px-4 py-2.5 text-xs text-muted">
            <span className="flex-1">
              {total ? `Showing ${first + 1}–${Math.min(first + size, total)} of ${total}` : "No items"}
            </span>
            <Button
              size="sm"
              disabled={page <= 1}
              onClick={() => url.set({ page: page - 1 > 1 ? page - 1 : null })}
            >
              Previous
            </Button>
            <Button size="sm" disabled={first + size >= total} onClick={() => url.set({ page: page + 1 })}>
              Next
            </Button>
          </div>
        </QueryState>
      </Box>
      {adding && (
        <AddToDatasetDialog
          experimentId={e.id}
          itemIds={[...selected]}
          onClose={() => setAdding(false)}
          onAdded={() => onSelect(new Set())}
        />
      )}
      {annotating && (
        <SendToQueueDialog
          experiment={{ id: e.id, name: e.name }}
          resultIds={[...selected]}
          onClose={() => setAnnotating(false)}
          onSent={() => onSelect(new Set())}
        />
      )}
    </>
  );
}
